import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { GicAnywhere, type IssueCreateEmbedSuccessPayload } from '@atlassian/gic-anywhere';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import type { AiIssueCreateState, StateSource } from '@atlassian/ai-issue-create';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import type {
	IssueCreateSidePanelBodyProps,
	SuggestedIssuesErrorMessage,
} from '../../__types__/IssueCreateSidePanelBody.ts';
import {
	PageMode,
	useAiIssueCreationEnablementState,
	useIssueCreateSidePanelContext,
} from '../../providers/IssueCreateSidePanelContextProvider.tsx';
import { IssueCreateAiLoading } from '../ai-work-create/IssueCreateAiLoading.tsx';
import { messages } from '../../messages.ts';
import {
	constructBreakContainerTag,
	constructSmartlinkTag,
	insertSingleWorkItemToEndOfSelection,
	refreshPageWithTimeout,
	updateLastFetchTimeIfNotViewMode,
} from '../../services/issueInsertionUtils.tsx';
import { useTriggerFlagIssueCreated } from '../../utils/useTriggerFlagIssueCreated.tsx';
import {
	getIsCreateIssueWithAiEnabledByUser,
	getPersistedOverrideCloudId,
	saveCloudIdToLocalStorage,
} from '../../utils/localStorageHelpers.ts';
import { IssueCreateWithAiToggle } from '../ai-work-create/IssueCreateWithAiToggle.tsx';
import { ConfluenceAiIssueCreate } from '../ai-work-create/ConfluenceAiIssueCreate.tsx';
import { handleAiIssueCreateError } from '../../utils/handleAiIssueCreateError.tsx';
import { useMessageListener } from '../../utils/useMessageListener.tsx';
import { WorkItemCreationError } from '../work-create-common/index.tsx';
import { useIsAIIssueCreationAllowed } from '../../utils/useIsAIIssueCreationAllowed.tsx';

const aiContentWrapperBaseStyles = xcss({
	display: 'inherit',
	flexGrow: 1,
});
const aiContentWrapperHiddenStyles = xcss({ display: 'none' });

export const IssueCreateSidePanelBody = ({
	onClose,
	flags,
}: IssueCreateSidePanelBodyProps): React.JSX.Element => {
	const { cloudId } = useSessionData();
	const [state, actions] = useIssueCreateSidePanelContext();
	const {
		contentId,
		contentType,
		summary,
		descriptionAdf,
		aiState,
		isCreatedWithAi,
		highlightedText,
		selectedAdf,
		insertStorageFragmentRequestPayload,
		totalRetries,
		firstAttempt,
		aiAnalyticsContext,
		currentSiteSelection,
		currentProjectSelection,
		isGicIframeReady,
		isCreatedFromActionItems,
		editorAPI,
	} = state;
	const aiIssueCreationEnablement = useAiIssueCreationEnablementState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { trackAIInteractionInit, trackAIResultError, trackAIResultView, trackAIResultAction } =
		useAIEventsInstrumentation();

	useIsAIIssueCreationAllowed({ contentId, isBulkCreate: false });

	const aiError = aiState === 'error';

	// no need to override if it's the same site
	const persistedOverrideCloudId = getPersistedOverrideCloudId();
	const [overrideCloudIdForSitePicker, setOverrideCloudIdForSitePicker] = useState(
		persistedOverrideCloudId !== cloudId ? persistedOverrideCloudId : undefined,
	);
	const [overrideProjectId, setOverrideProjectId] = useState<string | undefined>(undefined);

	const [showCreateWithAiToggle, setShowCreateWithAiToggle] = useState(false);

	const defaultErrorMessage = fg('confluence-issue-terminology-refresh')
		? messages.singleCreateAiErrorDescriptionIssueTermRefreshRebrandToRovo
		: messages.singleCreateAiErrorDescriptionRebrandToRovo;
	const [errorMessage, setErrorMessage] =
		useState<(typeof messages)[keyof typeof messages]>(defaultErrorMessage);
	const [errorMessageValues, setErrorMessageValues] = useState<Record<string, ReactNode>>();
	const [hideTryAgain, setHideTryAgain] = useState(false);

	useMessageListener();
	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'linkCreateScreen',
				attributes: {
					isManuallyCreated: !isCreatedWithAi,
					isCreatedWithAi,
					isCreatedFromActionItems,
					// We don't invoke table creation from highlighting a single row
					isCreatedFromTable: false,
					pageMode: state.pageMode,
					hasEditPermission: state.hasEditPermission,
					aiEnablement: aiIssueCreationEnablement?.enablement,
				},
				source: 'confluenceHighlightIssueCreate',
			},
		}).fire();
	}, [
		isCreatedWithAi,
		createAnalyticsEvent,
		aiIssueCreationEnablement?.enablement,
		state.pageMode,
		state.hasEditPermission,
		isCreatedFromActionItems,
	]);

	const isAiLoading = aiState === 'loading';
	const triggerFlag = useTriggerFlagIssueCreated(flags);

	const handleTryAgain = useCallback(() => {
		void actions.setTotalRetries();
		void actions.setFirstAttempt(false);
		void actions.setAiState('idle');

		// trigger a new AI interaction init event retries
		trackAIInteractionInit({
			attributes: {
				pageMode: state.pageMode,
				totalRetries: totalRetries + 1,
				firstAttempt: false,
				aiEnablement: aiIssueCreationEnablement?.enablement,
			},
		});
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'issueCreateSidePanelErrorScreen',
				action: 'retry',
				actionSubject: 'issueCreate',
				attributes: {
					pageMode: state.pageMode,
					hasEditPermission: state.hasEditPermission,
					createManually: !isCreatedWithAi,
					tryAgainWithAi: isCreatedWithAi,
					totalRetries: totalRetries + 1,
					firstAttempt: false,
					...aiAnalyticsContext,
				},
			},
		}).fire();
	}, [
		actions,
		trackAIInteractionInit,
		totalRetries,
		aiIssueCreationEnablement?.enablement,
		createAnalyticsEvent,
		state.pageMode,
		isCreatedWithAi,
		aiAnalyticsContext,
		state.hasEditPermission,
	]);

	const handleCreateManually = useCallback(() => {
		void actions.setIsCreatedWithAi(false);
		void actions.setAiState('idle');
		setShowCreateWithAiToggle(false);
	}, [actions]);

	const handleCreateWithAi = useCallback(() => {
		void actions.setIsCreatedWithAi(true);
		void actions.setAiState('loading');
		setShowCreateWithAiToggle(false);
	}, [actions]);

	const handleCreateManuallyAfterError = useCallback(() => {
		void actions.setTotalRetries();
		void actions.setFirstAttempt(false);
		void actions.setIsCreateManuallyPressed(true);
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'issueCreateSidePanelErrorScreen',
				action: 'retry',
				actionSubject: 'issueCreate',
				attributes: {
					pageMode: state.pageMode,
					hasEditPermission: state.hasEditPermission,
					createManually: true,
					tryAgainWithAi: false,
					totalRetries: totalRetries + 1,
					firstAttempt: false,
					...aiAnalyticsContext,
				},
			},
		}).fire();

		handleCreateManually();
	}, [
		actions,
		createAnalyticsEvent,
		state.pageMode,
		state.hasEditPermission,
		totalRetries,
		aiAnalyticsContext,
		handleCreateManually,
	]);

	const onIssueCreateInit = useCallback(() => {
		setShowCreateWithAiToggle(aiIssueCreationEnablement?.isEnabled ?? false);

		/*
		 * When ConfluenceAI=OFF & JiraAI=ON this component will manually create the issue first until
		 * selected site & project info is available to check Jira's AI enablement.  If JiraAI=ON then
		 * we can setIsCreatedWithAi to what the user's personal AI enablement to trigger a rerender.
		 */
		if (aiIssueCreationEnablement?.isEnabled) {
			void actions.setIsCreatedWithAi(getIsCreateIssueWithAiEnabledByUser());
		} else {
			void actions.setIsCreatedWithAi(false);
		}

		if (!getIsCreateIssueWithAiEnabledByUser()) {
			return;
		}

		/*
		 * requiresComponentChange is important since we are switching between manaul create (GicAnywhere) and
		 * ConfluenceAiIssueCreate (which uses GicAnywhere under the hood).  The GicAnywhere has limitations where
		 * changes to overrideCloudId after initial load can cause iframe rendering issues.  A component swap happens only when
		 * the isCreatedWithAi state is changed so we can bundle these prop updates together.
		 */
		const requiresComponentChange = aiIssueCreationEnablement?.isEnabled !== isCreatedWithAi;
		if (requiresComponentChange) {
			void setOverrideCloudIdForSitePicker(currentSiteSelection);
			void setOverrideProjectId(currentProjectSelection?.selectedProjectId);

			// During component switch the iframe will be replaced and there is no event for this.
			// We need to reset it here manually.
			void actions.setIsGicIframeReady(false);
		}

		if (currentSiteSelection && currentSiteSelection !== overrideCloudIdForSitePicker) {
			void setOverrideProjectId(currentProjectSelection?.selectedProjectId);
		}
	}, [
		aiIssueCreationEnablement?.isEnabled,
		isCreatedWithAi,
		currentSiteSelection,
		overrideCloudIdForSitePicker,
		actions,
		currentProjectSelection?.selectedProjectId,
	]);

	const onAiIssueCreateInit = useCallback(() => {
		onIssueCreateInit();
		const analyticAttributes = {
			attributes: {
				...aiAnalyticsContext,
				aiEnablement: aiIssueCreationEnablement?.enablement,
				pageMode: state.pageMode,
				hasEditPermission: state.hasEditPermission,
			},
		};
		trackAIInteractionInit(analyticAttributes);
		trackAIResultView({
			actionSubjectId: 'aiIssueCreatOnInitComplete',
			...analyticAttributes,
		});
	}, [
		onIssueCreateInit,
		aiAnalyticsContext,
		aiIssueCreationEnablement?.enablement,
		state.pageMode,
		trackAIInteractionInit,
		trackAIResultView,
		state.hasEditPermission,
	]);

	const isSuggestedIssuesErrorMessage = (error: any): error is SuggestedIssuesErrorMessage => {
		return error && typeof error === 'object' && 'message_template' in error;
	};

	const onFailure = useCallback(
		(error: SuggestedIssuesErrorMessage | Error | unknown) => {
			// reset
			if (hideTryAgain) {
				setHideTryAgain(false);
				setErrorMessage(
					fg('confluence-issue-terminology-refresh')
						? messages.singleCreateAiErrorDescriptionIssueTermRefreshRebrandToRovo
						: messages.singleCreateAiErrorDescriptionRebrandToRovo,
				);
				setErrorMessageValues({});
			}

			if (isSuggestedIssuesErrorMessage(error)) {
				const aiIssueCreateError = handleAiIssueCreateError(error);

				if (aiIssueCreateError !== undefined) {
					setErrorMessage(aiIssueCreateError.errorMessage);
					setErrorMessageValues(aiIssueCreateError.errorMessageValues);
					setHideTryAgain(aiIssueCreateError.hideTryAgain);
				}
			}

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'confluenceHighlightIssueCreate',
					action: 'error',
					actionSubject: 'globalIssueCreate',
					attributes: {
						isManuallyCreated: !isCreatedWithAi,
						isCreatedWithAi,
						pageMode: state.pageMode,
						hasEditPermission: state.hasEditPermission,
						error,
						aiEnablement: aiIssueCreationEnablement?.enablement,
					},
				},
			}).fire();
			if (isCreatedWithAi) {
				trackAIResultError(
					{
						aiErrorMessage: (error as Error).message,
					},
					{
						attributes: {
							pageMode: state.pageMode,
							hasEditPermission: state.hasEditPermission,
							totalRetries,
							firstAttempt,
							...aiAnalyticsContext,
						},
					},
				);
			}
		},
		[
			hideTryAgain,
			createAnalyticsEvent,
			isCreatedWithAi,
			state.pageMode,
			state.hasEditPermission,
			aiIssueCreationEnablement?.enablement,
			trackAIResultError,
			totalRetries,
			firstAttempt,
			aiAnalyticsContext,
		],
	);

	const onSuccess = useCallback(
		(payload: IssueCreateEmbedSuccessPayload) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'confluenceHighlightIssueCreate',
					action: 'created',
					actionSubject: 'issue',
					actionSubjectId: 'confluenceIssueCreate',
					attributes: {
						isManuallyCreated: !isCreatedWithAi,
						isCreatedWithAi,
						// We don't invoke table creation from highlighting a single row
						isCreatedFromTable: false,
						isCreatedFromActionItems,
						pageMode: state.pageMode,
						hasEditPermission: state.hasEditPermission,
						aiEnablement: aiIssueCreationEnablement?.enablement,
					},
				},
			}).fire();
			if (isCreatedWithAi) {
				trackAIResultAction('issue created', {
					attributes: {
						pageMode: state.pageMode,
						hasEditPermission: state.hasEditPermission,
						isCreatedFromActionItems,
						// We don't invoke table creation from highlighting a single row
						isCreatedFromTable: false,
						totalRetries,
						firstAttempt,
						issueId: payload?.issueId || '',
						aiEnablement: aiIssueCreationEnablement?.enablement,
						...aiAnalyticsContext,
					},
				});
			}
			triggerFlag(
				payload,
				'success-circle',
				fg('confluence-issue-terminology-refresh')
					? messages.sidePanelSingleIssueCreateSuccessFlagTitleIssueTermRefresh
					: messages.sidePanelSingleIssueCreateSuccessFlagTitle,
			);

			// Save the cloud id in localStorage if an issue is created
			saveCloudIdToLocalStorage(payload);

			if (insertStorageFragmentRequestPayload && state.hasEditPermission) {
				insertStorageFragmentRequestPayload.xmlModification =
					isCreatedFromActionItems && fg('confluence-create-work-items-from-action-items')
						? constructBreakContainerTag(constructSmartlinkTag(payload.link))
						: constructSmartlinkTag(payload.link);

				updateLastFetchTimeIfNotViewMode(insertStorageFragmentRequestPayload, state.pageMode);

				const insertPromise = insertSingleWorkItemToEndOfSelection({
					insertStorageFragmentRequestPayload,
					pageMode: state.pageMode ?? PageMode.VIEW,
					selectedAdf,
					editorAPI,
					workItemUrl: payload.link,
				});

				void insertPromise
					.then((data) => {
						// There is a case where the API response is OK, but the response data is false.
						// When this happens, the backend has trouble inserting the ticket to the page and we should show an error toast.
						if (data === false) {
							throw new Error('Failed to insert storage fragment');
						}

						return createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								source: 'confluenceHighlightIssueCreate',
								action: 'created',
								actionSubject: 'link',
								actionSubjectId: 'confluenceIssueCreate',
								attributes: {
									isManuallyCreated: !isCreatedWithAi,
									isCreatedWithAi,
									pageMode: state.pageMode,
									hasEditPermission: state.hasEditPermission,
								},
							},
						}).fire();
					})
					.then(() => {
						// Refresh the page only if the current mode is VIEW, as other modes receive live updates.
						if (state.pageMode === PageMode.VIEW) {
							refreshPageWithTimeout();
						}
					})
					.catch((_error) =>
						triggerFlag(payload, 'error', messages.sidePanelIssueCreateInsertionFailed),
					);
			}
		},
		[
			createAnalyticsEvent,
			isCreatedWithAi,
			isCreatedFromActionItems,
			state.pageMode,
			state.hasEditPermission,
			aiIssueCreationEnablement?.enablement,
			triggerFlag,
			insertStorageFragmentRequestPayload,
			selectedAdf,
			editorAPI,
			trackAIResultAction,
			totalRetries,
			firstAttempt,
			aiAnalyticsContext,
		],
	);

	const handleStateChange = useCallback(
		(state: AiIssueCreateState, source: StateSource) => {
			// Keep loading animation active when get Confluence page completes but field suggestions are still loading
			if (
				source === 'aiIssueCreateGetConfluencePageState' &&
				(state === 'idle' || state === 'loading')
			) {
				return;
			}
			void actions.setAiState(state);
		},
		[actions],
	);

	const onAiToggleChange = useCallback(
		(enabled: boolean) => {
			if (enabled) {
				trackAIInteractionInit({
					attributes: {
						pageMode: state.pageMode,
						hasEditPermission: state.hasEditPermission,
						aiEnablement: aiIssueCreationEnablement?.enablement,
					},
				});
				handleCreateWithAi();
			} else {
				handleCreateManually();
			}
		},
		[
			trackAIInteractionInit,
			state.pageMode,
			state.hasEditPermission,
			aiIssueCreationEnablement?.enablement,
			handleCreateWithAi,
			handleCreateManually,
		],
	);

	if (aiError) {
		return (
			<WorkItemCreationError
				creationSource="confluenceHighlightIssueCreate"
				errorMessage={errorMessage}
				errorMessageValues={hideTryAgain ? errorMessageValues : undefined}
				onTryAgain={hideTryAgain ? undefined : handleTryAgain}
				onCreateManually={handleCreateManuallyAfterError}
				isCreatedWithAi
			/>
		);
	}

	return (
		<>
			{!isAiLoading && isGicIframeReady && showCreateWithAiToggle && (
				<IssueCreateWithAiToggle onChange={onAiToggleChange} />
			)}
			{isCreatedWithAi ? (
				<>
					{isAiLoading && <IssueCreateAiLoading />}
					<Box xcss={[aiContentWrapperBaseStyles, isAiLoading && aiContentWrapperHiddenStyles]}>
						<ConfluenceAiIssueCreate
							cloudId={cloudId}
							contentType={contentType}
							pageId={contentId}
							highlightedTextAdf={highlightedText}
							consumer="confluence-highlight-issue-create-ai"
							onInit={onAiIssueCreateInit}
							onSuccess={onSuccess}
							onFailure={onFailure}
							onClose={onClose}
							onStateChange={handleStateChange}
							overrideCloudIdForSitePicker={overrideCloudIdForSitePicker}
							overrideProjectIdForProjectPicker={overrideProjectId}
							setAiAnalyticsContext={actions.setAiAnalyticsContext}
							aIIssueCreationEnablement={aiIssueCreationEnablement}
						/>
					</Box>
				</>
			) : (
				<GicAnywhere
					cloudId={cloudId}
					consumer="confluence-highlight-issue-create"
					onInit={onIssueCreateInit}
					onSuccess={onSuccess}
					onClose={onClose}
					onFailure={onFailure}
					summary={summary}
					descriptionAdf={descriptionAdf}
					overrideCloudId={overrideCloudIdForSitePicker}
					isPayloadReady
					projectId={overrideProjectId}
				/>
			)}
		</>
	);
};
